import { Alert, Badge, Button, Checkbox, Group, MultiSelect, Paper, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { rawApi } from '../api/client';
import { errorCopy } from '../api/errors';

const DISCOUNTS_PATH = '/admin/v1/pricing/discounts';
const CATEGORIES_PATH = '/admin/v1/catalog/categories';
const ITEMS_PATH = '/admin/v1/catalog/items';

interface EligibleRule {
  targetType: 'category' | 'menuItem';
  targetId: string;
  effect: 'include' | 'exclude';
}

interface DiscountRow {
  id: string;
  nameEn: string;
  isStaffBenefit: boolean;
}

interface CategoryRow {
  id: string;
  nameEn: string;
  kind: string;
  active: boolean;
}

interface ItemRow {
  id: string;
  nameEn: string;
  categoryId: string;
}

/**
 * WHAT THE STAFF MEAL COVERS, in plain words, editable by the owner.
 *
 * The list fails closed on the register: an item on no rule here is full price. So this screen's one
 * job is to make the eligible set readable at a glance — whole categories ticked, single items
 * added or held out by name — and to say out loud that anything unmentioned is not covered. No
 * catalog flag could express the owner's sentence ("50% on food, local beers, basic drinks"):
 * Local Beer IS alcohol and a mocktail is NOT, which is exactly why this is an explicit list.
 */
export function StaffMealEligible() {
  const queryClient = useQueryClient();
  const discounts = useQuery({
    queryKey: ['pricing-discounts-staff-benefit'],
    queryFn: () => rawApi<DiscountRow[]>('GET', DISCOUNTS_PATH),
  });
  const staffBenefit = (discounts.data ?? []).filter((d) => d.isStaffBenefit);

  return (
    <>
      {staffBenefit.map((discount) => (
        <EligibleEditor
          key={discount.id}
          discount={discount}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['staff-meal-eligible', discount.id] })}
        />
      ))}
    </>
  );
}

function EligibleEditor({ discount, onSaved }: { discount: DiscountRow; onSaved: () => void }) {
  const rules = useQuery({
    queryKey: ['staff-meal-eligible', discount.id],
    queryFn: () =>
      rawApi<{ rules: (EligibleRule & { targetName: string })[] }>(
        'GET',
        `${DISCOUNTS_PATH}/${discount.id}/eligible-items`,
      ),
  });
  const categories = useQuery({
    queryKey: ['catalog-categories-for-eligible'],
    queryFn: () => rawApi<CategoryRow[]>('GET', CATEGORIES_PATH),
  });
  const items = useQuery({
    queryKey: ['catalog-items-for-eligible'],
    queryFn: () => rawApi<ItemRow[]>('GET', ITEMS_PATH),
  });

  const [includedCategories, setIncludedCategories] = useState<string[]>([]);
  const [includedItems, setIncludedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!rules.data) return;
    setIncludedCategories(
      rules.data.rules
        .filter((r) => r.targetType === 'category' && r.effect === 'include')
        .map((r) => r.targetId),
    );
    setIncludedItems(
      rules.data.rules
        .filter((r) => r.targetType === 'menuItem' && r.effect === 'include')
        .map((r) => r.targetId),
    );
    setExcludedItems(
      rules.data.rules
        .filter((r) => r.targetType === 'menuItem' && r.effect === 'exclude')
        .map((r) => r.targetId),
    );
    setDirty(false);
  }, [rules.data]);

  const save = useMutation({
    mutationFn: () =>
      rawApi('PUT', `${DISCOUNTS_PATH}/${discount.id}/eligible-items`, {
        rules: [
          ...includedCategories.map(
            (id): EligibleRule => ({ targetType: 'category', targetId: id, effect: 'include' }),
          ),
          ...includedItems.map(
            (id): EligibleRule => ({ targetType: 'menuItem', targetId: id, effect: 'include' }),
          ),
          ...excludedItems.map(
            (id): EligibleRule => ({ targetType: 'menuItem', targetId: id, effect: 'exclude' }),
          ),
        ],
      }),
    onSuccess: () => {
      notifications.show({ message: `Saved what ${discount.nameEn} covers.`, color: 'teal' });
      setDirty(false);
      onSaved();
    },
    onError: (err) => {
      const copy = errorCopy(err, 'save the covered items');
      notifications.show({ title: copy.title, message: copy.message, color: 'red' });
    },
  });

  const itemOptions = useMemo(
    () => (items.data ?? []).map((i) => ({ value: i.id, label: i.nameEn })),
    [items.data],
  );
  const categoryNameOf = useMemo(
    () => new Map((categories.data ?? []).map((c) => [c.id, c.nameEn])),
    [categories.data],
  );
  const itemNameOf = useMemo(() => new Map((items.data ?? []).map((i) => [i.id, i.nameEn])), [items.data]);

  // The plain-words sentence the whole screen exists for.
  const summary = useMemo(() => {
    const cats = includedCategories.map((id) => categoryNameOf.get(id) ?? id);
    const adds = includedItems.map((id) => itemNameOf.get(id) ?? id);
    const outs = excludedItems.map((id) => itemNameOf.get(id) ?? id);
    if (cats.length === 0 && adds.length === 0) return 'Nothing is covered yet — every item refuses.';
    const parts = [];
    if (cats.length > 0) parts.push(`everything in ${cats.join(', ')}`);
    if (adds.length > 0) parts.push(adds.join(', '));
    let sentence = `Covers ${parts.join('; plus ')}`;
    if (outs.length > 0) sentence += ` — except ${outs.join(', ')}`;
    return `${sentence}. Anything not named here is full price.`;
  }, [includedCategories, includedItems, excludedItems, categoryNameOf, itemNameOf]);

  return (
    <Paper withBorder p="md" radius="md" mt="md">
      <Group gap="xs">
        <Title order={5}>What {discount.nameEn} covers</Title>
        <Badge variant="light" color="teal">
          Staff benefit
        </Badge>
      </Group>
      <Text size="sm" c="dimmed" mt={2} maw={880}>
        The staff meal only ever applies to what is named here. A new category — or next season's cocktail —
        stays full price until you add it, and the register refuses anything off this list on the spot.
      </Text>

      {rules.isError && (
        <Alert color="red" mt="sm">
          That list could not be loaded.
        </Alert>
      )}

      <Alert color="gray" variant="light" mt="sm">
        <Text size="sm">{summary}</Text>
      </Alert>

      <Stack gap="xs" mt="md">
        <Text size="sm" fw={500}>
          Whole categories
        </Text>
        <Group gap="md">
          {(categories.data ?? [])
            .filter((c) => c.active)
            .map((c) => (
              <Checkbox
                key={c.id}
                label={c.nameEn}
                checked={includedCategories.includes(c.id)}
                onChange={(e) => {
                  setDirty(true);
                  setIncludedCategories((current) =>
                    e.currentTarget.checked ? [...current, c.id] : current.filter((id) => id !== c.id),
                  );
                }}
              />
            ))}
        </Group>

        <MultiSelect
          label="Single items also covered"
          description="Items whose category is not ticked above — the local beer inside Beer & Liquor, the coffee inside Drinks."
          data={itemOptions}
          value={includedItems}
          onChange={(value) => {
            setDirty(true);
            setIncludedItems(value);
          }}
          searchable
          clearable
        />
        <MultiSelect
          label="Items held out"
          description="Items inside a ticked category that stay full price anyway."
          data={itemOptions}
          value={excludedItems}
          onChange={(value) => {
            setDirty(true);
            setExcludedItems(value);
          }}
          searchable
          clearable
        />
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
          Save what it covers
        </Button>
      </Group>
    </Paper>
  );
}
